import { Box, Button, HStack, Image, SimpleGrid, Stack, Text } from "@chakra-ui/react"
import { Form } from "components"
import { API, Mask, toaster } from "config"
import useDistributor from "hooks/useDistributor"
import { useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"

const card = {
    background: '#161616',
    border: '1px solid #2a2a2a',
    borderRadius: '14px',
    padding: '24px',
}

const cardHeader = {
    borderBottom: '1px solid #2a2a2a',
    paddingBottom: '12px',
    marginBottom: '4px',
}

export default function AtualizarDados({ daysRemaining, overdue, onSkip }) {

    const queryClient = useQueryClient()
    const { data: userData, isLoading: userLoading } = useDistributor()
    const [form, setForm] = useState({})
    const [errors, setErrors] = useState({})
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (userData && !form.email) {
            setForm({
                email:       userData.email ?? '',
                mobile:      Mask.mobile(userData.mobile ?? ''),
                work_number: Mask.mobile(userData.work_number ?? ''),
            })
        }
    }, [userData])

    const handleChange = ({ target }) => {
        const { name, value } = target
        const phoneFieds = ['mobile', 'work_number']
        setForm(prev => ({
            ...prev,
            [name]: phoneFieds.includes(name) ? Mask.mobile(value) : value,
        }))
    }

    const handleSubmit = async () => {
        setLoading(true)
        setErrors({})

        try {
            const payload = {
                email:       form.email,
                mobile:      form.mobile?.replace(/\D/g, '') ?? '',
                work_number: form.work_number?.replace(/\D/g, '') || null,
            }
            await API.put('/me/mandatory-update', payload)
            toaster.create({ title: 'Dados atualizados com sucesso!', type: 'success' })
            queryClient.invalidateQueries({ queryKey: ['distributor'] })
        } catch (err) {
            const responseErrors = err?.response?.data?.errors ?? {}
            setErrors(responseErrors)
            toaster.create({
                title: 'Não foi possível salvar.',
                description: err?.response?.data?.message,
                type: 'warning',
            })
        }

        setLoading(false)
    }

    if (userLoading) return (
        <Box
            style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            className="chakra-theme dark"
        >
            <Stack align="center" gap={4}>
                <Image src="/icon.png" h="56px" borderRadius="lg" style={{ mixBlendMode: 'screen' }} />
                <Text color="gray.400" fontSize="sm">Carregando seus dados...</Text>
            </Stack>
        </Box>
    )

    return (
        <Box style={{ minHeight: '100vh', background: '#0a0a0a' }} className="chakra-theme dark">

            <Box style={{ background: '#111111', borderBottom: '1px solid #222222' }} px={6} py={3}>
                <HStack maxW="1100px" mx="auto" justify="space-between">
                    <HStack gap={3}>
                        <Image src="/icon.png" h="34px" borderRadius="md" style={{ mixBlendMode: 'screen' }} />
                        <Text color="white" fontWeight="bold" fontSize="md" letterSpacing="tight">
                            Bonífica
                        </Text>
                    </HStack>
                    <Text color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                        Área do Núcleo
                    </Text>
                </HStack>
            </Box>

            <Box maxW="1100px" mx="auto" py={6} px={4}>

                <Stack gap={1} mb={5}>
                    <HStack gap={3} align="center">
                        <Box style={{ width: '3px', height: '28px', background: '#f97316', borderRadius: '2px', flexShrink: 0 }} />
                        <Text color="white" fontWeight="bold" fontSize="xl">
                            Confirmação de dados cadastrais
                        </Text>
                    </HStack>
                    <Text color="gray.500" fontSize="sm" pl="15px">
                        Preencha os campos abaixo para liberar o acesso completo à plataforma
                    </Text>
                </Stack>

                {!overdue && daysRemaining !== null && daysRemaining !== undefined && (
                    <Box
                        style={{
                            background: daysRemaining <= 1 ? '#1a0a00' : '#111800',
                            border: `1px solid ${daysRemaining <= 1 ? '#f97316' : '#84cc16'}`,
                            borderRadius: '10px',
                            padding: '12px 16px',
                            marginBottom: '20px',
                        }}
                    >
                        <HStack gap={2}>
                            <Text fontSize="sm" style={{ flexShrink: 0 }}>{daysRemaining <= 1 ? '⚠️' : '📅'}</Text>
                            <Text color={daysRemaining <= 1 ? 'orange.300' : 'green.300'} fontSize="sm">
                                {daysRemaining === 0
                                    ? 'Último dia para atualizar seus dados antes de ser obrigatório.'
                                    : `Você tem ${daysRemaining} dia${daysRemaining !== 1 ? 's' : ''} para atualizar seus dados voluntariamente.`
                                }
                            </Text>
                        </HStack>
                    </Box>
                )}

                {overdue && (
                    <Box
                        style={{
                            background: '#1a0000',
                            border: '1px solid #ef4444',
                            borderRadius: '10px',
                            padding: '12px 16px',
                            marginBottom: '20px',
                        }}
                    >
                        <HStack gap={2}>
                            <Text fontSize="sm" style={{ flexShrink: 0 }}>🔒</Text>
                            <Text color="red.300" fontSize="sm">
                                O prazo para atualização voluntária encerrou. A confirmação dos dados é obrigatória para continuar.
                            </Text>
                        </HStack>
                    </Box>
                )}

                <Form.Container onSubmit={handleSubmit}>
                    <Stack gap={5}>

                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>

                            {/* Contato */}
                            <Box style={card}>
                                <Stack gap={4}>
                                    <HStack gap={2} style={cardHeader}>
                                        <Box style={{ width: '2px', height: '14px', background: '#f97316', borderRadius: '1px' }} />
                                        <Text fontSize="xs" fontWeight="bold" color="orange.400" textTransform="uppercase" letterSpacing="widest">
                                            Contato
                                        </Text>
                                    </HStack>

                                    <Form.Text
                                        required
                                        label="E-mail"
                                        name="email"
                                        type="email"
                                        value={form.email ?? ''}
                                        onChange={handleChange}
                                        error={errors?.email}
                                        disabled={loading}
                                        placeholder="contato@nucleo.com.br"
                                    />
                                    <HStack gap={2} align="flex-start" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '10px 12px' }}>
                                        <Text style={{ flexShrink: 0, fontSize: '13px', marginTop: '1px' }}>⚠️</Text>
                                        <Text color="gray.400" fontSize="xs" lineHeight="tall">
                                            O e-mail é usado para login — alterá-lo mudará as credenciais de acesso.{' '}
                                            <Text as="span" color="gray.300">Utilize um endereço válido e de acesso frequente.</Text>
                                        </Text>
                                    </HStack>

                                    <SimpleGrid columns={2} gap={3}>
                                        <Form.Text
                                            required
                                            label="Celular / WhatsApp"
                                            name="mobile"
                                            type="tel"
                                            value={form.mobile ?? ''}
                                            onChange={handleChange}
                                            error={errors?.mobile}
                                            disabled={loading}
                                            placeholder="Ex.: 11999999999"
                                        />
                                        <Form.Text
                                            label="Tel. comercial"
                                            name="work_number"
                                            type="tel"
                                            value={form.work_number ?? ''}
                                            onChange={handleChange}
                                            error={errors?.work_number}
                                            disabled={loading}
                                            placeholder="Ex.: 1132001234"
                                        />
                                    </SimpleGrid>
                                </Stack>
                            </Box>

                            {/* Endereço e Dados Fiscais — somente leitura */}
                            <Box style={card}>
                                <Stack gap={4}>
                                    <HStack gap={2} style={cardHeader}>
                                        <Box style={{ width: '2px', height: '14px', background: '#f97316', borderRadius: '1px' }} />
                                        <Text fontSize="xs" fontWeight="bold" color="orange.400" textTransform="uppercase" letterSpacing="widest">
                                            Endereço e Dados Fiscais
                                        </Text>
                                    </HStack>

                                    <HStack gap={3} align="flex-start" style={{ background: '#1c1208', border: '1px solid #f97316', borderRadius: '10px', padding: '14px 16px' }}>
                                        <Text style={{ flexShrink: 0, fontSize: '20px', lineHeight: 1 }}>🔒</Text>
                                        <Stack gap={1}>
                                            <Text color="orange.300" fontSize="sm" fontWeight="bold" lineHeight="short">
                                                Estes dados não podem ser alterados aqui
                                            </Text>
                                            <Text color="gray.400" fontSize="xs" lineHeight="tall">
                                                Para atualizar endereço, CNPJ ou Inscrição Estadual, entre em contato com o{' '}
                                                <Text as="span" color="orange.300" fontWeight="semibold">departamento de núcleos</Text>.
                                            </Text>
                                        </Stack>
                                    </HStack>

                                    <Box style={{ background: '#111111', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '14px 16px' }}>
                                        <Stack gap={2}>
                                            <Text color="gray.600" fontSize="10px" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
                                                Endereço cadastrado
                                            </Text>
                                            {userData?.city ? (
                                                <Stack gap={1}>
                                                    <Text color="gray.300" fontSize="sm">
                                                        {[userData.address_line_1, userData.address_number].filter(Boolean).join(', ')}
                                                        {userData.address_complement ? ` — ${userData.address_complement}` : ''}
                                                    </Text>
                                                    <Text color="gray.400" fontSize="xs">
                                                        {[userData.address_neighborhood, userData.city?.name, userData.city?.state?.name].filter(Boolean).join(', ')}
                                                        {userData.zip ? ` · CEP ${Mask.zip(userData.zip)}` : ''}
                                                    </Text>
                                                </Stack>
                                            ) : (
                                                <Text color="gray.600" fontSize="sm" fontStyle="italic">Nenhum endereço cadastrado</Text>
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>

                        </SimpleGrid>

                        <SimpleGrid columns={onSkip ? 2 : 1} gap={3}>
                            {onSkip && (
                                <Button
                                    size="lg"
                                    onClick={onSkip}
                                    style={{
                                        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '15px',
                                        height: '52px',
                                        borderRadius: '10px',
                                        letterSpacing: '0.3px',
                                    }}
                                >
                                    Pular Etapa
                                </Button>
                            )}
                            <Button
                                type="submit"
                                size="lg"
                                loading={loading}
                                loadingText="Salvando..."
                                style={{
                                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: '15px',
                                    height: '52px',
                                    borderRadius: '10px',
                                    letterSpacing: '0.3px',
                                }}
                            >
                                Confirmar meus dados →
                            </Button>
                        </SimpleGrid>

                    </Stack>
                </Form.Container>
            </Box>

        </Box>
    )
}
